<div class="page-head">
  <div>
    <p class="eyebrow">การควบคุมค่าคอมมิชชั่น</p>
    <h1>ยอดหักคืนค่าคอม</h1>
    <p>ติดตามยอดค่าคอมที่ต้องหักคืน ยอดที่หักแล้ว และประวัติการนำยอดไปใช้</p>
  </div>
  @if (canExportExcel()) {
    <button
      type="button"
      class="tw-button inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] active:scale-[0.98]"
      (click)="exportExcel()"
    >
      <lucide-icon name="Download" [size]="17" />
      Export Excel
    </button>
  }
</div>

<section class="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
  <div class="mb-4">
    <h2 class="m-0 text-base font-bold text-slate-800">ตัวกรองรายการ</h2>
    <p class="mt-1 mb-0 text-xs text-slate-500">
      เลือกช่วงวันที่และสถานะยอดหักคืน
    </p>
  </div>

  <div class="grid gap-3 md:grid-cols-3">
    <label class="block">
      <span class="mb-1.5 block text-xs font-semibold text-slate-600">วันที่เริ่มต้น</span>
      <p-datepicker
        class="w-full"
        [ngModel]="pendingDateFrom()"
        (ngModelChange)="pendingDateFrom.set($event)"
        dateFormat="dd/mm/yy"
        placeholder="เลือกวันที่เริ่มต้น"
        [showIcon]="true"
        [showClear]="true"
        [readonlyInput]="true"
        [maxDate]="pendingDateTo()"
        appendTo="body"
      />
    </label>
    <label class="block">
      <span class="mb-1.5 block text-xs font-semibold text-slate-600">วันที่สิ้นสุด</span>
      <p-datepicker
        class="w-full"
        [ngModel]="pendingDateTo()"
        (ngModelChange)="pendingDateTo.set($event)"
        dateFormat="dd/mm/yy"
        placeholder="เลือกวันที่สิ้นสุด"
        [showIcon]="true"
        [showClear]="true"
        [readonlyInput]="true"
        [minDate]="pendingDateFrom()"
        appendTo="body"
      />
    </label>
    <label class="block">
      <span class="mb-1.5 block text-xs font-semibold text-slate-600">สถานะยอดหักคืน</span>
      <p-select
        class="w-full"
        [options]="statusOptions"
        [ngModel]="pendingStatus()"
        (ngModelChange)="pendingStatus.set($event)"
        optionLabel="label"
        optionValue="value"
        placeholder="เลือกสถานะ"
      />
    </label>
  </div>

  <div class="mt-4 flex flex-wrap justify-center gap-2">
    <button
      type="button"
      class="tw-button inline-flex items-center justify-center gap-1.5 rounded-lg bg-[var(--theme-color)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] active:scale-[0.98]"
      (click)="applyFilters()"
    >
      <lucide-icon name="Search" [size]="16" /> ค้นหา
    </button>
    <button
      type="button"
      class="tw-button inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
      (click)="resetFilters()"
    >
      <lucide-icon name="RotateCcw" [size]="15" /> ล้างค่า
    </button>
  </div>
</section>

<section class="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
  <div
    class="flex flex-col gap-4 border-b border-slate-200 px-5 py-4 sm:px-6 lg:flex-row lg:items-end lg:justify-between"
  >
    <div>
      <h2 class="m-0 text-base font-bold text-slate-800">รายการยอดหักคืนค่าคอม</h2>
      <p class="mt-1 mb-0 text-xs text-slate-500">
        แสดงยอดค่าคอมที่ต้องหักคืนและประวัติการนำยอดไปหักตามสิทธิ์ผู้ใช้งาน
      </p>
    </div>
    <div class="flex w-full flex-wrap items-center gap-2 lg:w-auto lg:justify-end">
      <span class="rounded-full bg-slate-100 px-2.5 py-1.5 text-[10px] font-semibold text-slate-600">
        {{ filteredRows().length }} รายการ
      </span>
      <label class="relative block w-full sm:w-96">
        <lucide-icon
          name="Search"
          [size]="16"
          class="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-slate-400"
        />
        <input
          pInputText
          type="search"
          class="h-10 w-full pr-3 text-sm placeholder:text-slate-400"
          style="padding-left: 2.75rem; border-radius: 0.5rem"
          placeholder="ค้นหาผู้รับคอม Booking เลขที่คอม หรือเลขคืนเงิน"
          [ngModel]="searchTerm()"
          (ngModelChange)="searchTerm.set($event)"
        />
      </label>
    </div>
  </div>

  <p-table
    [value]="filteredRows()"
    dataKey="id"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [rowHover]="true"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="แสดง {first} ถึง {last} จาก {totalRecords} รายการ"
    [tableStyle]="{ 'min-width': '1520px' }"
  >
    <ng-template #header>
      <tr>
        <th class="w-14 px-5 py-3 text-center font-semibold whitespace-nowrap">ลำดับ</th>
        <th pSortableColumn="recipientUser.firstName" class="min-w-56 px-5 py-3 font-semibold whitespace-nowrap">
          ผู้รับคอม <p-sortIcon field="recipientUser.firstName" />
        </th>
        <th pSortableColumn="booking.bookingNo" class="min-w-64 px-5 py-3 font-semibold whitespace-nowrap">
          Booking ต้นทาง <p-sortIcon field="booking.bookingNo" />
        </th>
        <th pSortableColumn="bookingCommission.commissionNo" class="min-w-52 px-5 py-3 font-semibold whitespace-nowrap">
          คอมเดิม <p-sortIcon field="bookingCommission.commissionNo" />
        </th>
        <th pSortableColumn="recoveryAmount" class="w-40 px-5 py-3 text-right font-semibold whitespace-nowrap">
          ยอดต้องหัก <p-sortIcon field="recoveryAmount" />
        </th>
        <th pSortableColumn="settledAmount" class="w-40 px-5 py-3 text-right font-semibold whitespace-nowrap">
          หักแล้ว <p-sortIcon field="settledAmount" />
        </th>
        <th pSortableColumn="remainingAmount" class="w-40 px-5 py-3 text-right font-semibold whitespace-nowrap">
          คงเหลือ <p-sortIcon field="remainingAmount" />
        </th>
        <th pSortableColumn="status" class="w-40 px-5 py-3 text-center font-semibold whitespace-nowrap">
          สถานะ <p-sortIcon field="status" />
        </th>
        <th pSortableColumn="createdAt" class="w-40 px-5 py-3 font-semibold whitespace-nowrap">
          วันที่ <p-sortIcon field="createdAt" />
        </th>
        <th class="w-28 px-5 py-3 text-center font-semibold whitespace-nowrap">ประวัติการหัก</th>
      </tr>
    </ng-template>
    <ng-template #body let-row let-rowIndex="rowIndex">
      <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/50">
        <td class="px-5 py-3.5 text-center text-[13px] text-slate-400 tabular-nums align-middle">{{ rowIndex + 1 }}</td>
        <td class="px-5 py-3.5 align-top">
          <p class="m-0 text-sm font-semibold text-slate-800">
            {{ row.recipientUser.firstName }} {{ row.recipientUser.lastName }}
          </p>
          <p class="mt-1 mb-0 text-xs text-slate-500">{{ row.recipientUser.employeeCode }}</p>
        </td>
        <td class="px-5 py-3.5 align-top">
          <a
            [routerLink]="['/bookings', row.booking.id]"
            class="text-sm font-semibold text-sky-700 no-underline hover:underline"
          >{{ row.booking.bookingNo }}</a>
          <p class="mt-1 mb-0 max-w-64 truncate text-xs text-slate-500">{{ row.booking.tourName }}</p>
          <p class="mt-1 mb-0 text-xs text-slate-500">{{ row.bookingRefund.refundNo }}</p>
        </td>
        <td class="px-5 py-3.5 align-top">
          <p class="m-0 text-sm font-semibold text-slate-800">{{ row.bookingCommission.commissionNo }}</p>
          <p class="mt-1 mb-0 text-xs text-slate-500">
            {{ row.bookingCommission.recipientRole === 'CARETAKER' ? 'ผู้ดูแลรายการ' : 'พนักงานขาย' }}
          </p>
          <p class="mt-1 mb-0 text-xs font-medium text-slate-700">
            {{ row.originalAmount | number: '1.2-2' }} บาท
          </p>
        </td>
        <td class="px-5 py-3.5 text-right text-sm font-semibold whitespace-nowrap text-red-600 tabular-nums align-top">
          {{ row.recoveryAmount | number: '1.2-2' }} บาท
        </td>
        <td class="px-5 py-3.5 text-right text-sm font-semibold whitespace-nowrap text-emerald-700 tabular-nums align-top">
          {{ row.settledAmount | number: '1.2-2' }} บาท
        </td>
        <td class="px-5 py-3.5 text-right text-sm font-bold whitespace-nowrap text-amber-700 tabular-nums align-top">
          {{ row.remainingAmount | number: '1.2-2' }} บาท
        </td>
        <td class="px-5 py-3.5 text-center align-top">
          <app-status-badge
            [status]="row.status"
            [displayLabel]="recoveryStatusLabel(row.status)"
            [tone]="recoveryStatusTone(row.status)"
          />
        </td>
        <td class="px-5 py-3.5 text-sm whitespace-nowrap text-slate-600 align-top">
          {{ row.createdAt | date: 'dd MMM yyyy' }}
        </td>
        <td class="px-5 py-3.5 text-center align-top">
          <button
            type="button"
            class="tw-button inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700"
            title="ดูประวัติการหัก"
            (click)="openHistory(row)"
          >
            <lucide-icon name="History" [size]="15" />
            {{ row.applications.length }}
          </button>
        </td>
      </tr>
    </ng-template>
    <ng-template #emptymessage>
      <tr>
        <td colspan="10" class="px-5 py-12 text-center">
          <div class="mx-auto grid size-12 place-items-center rounded-full bg-slate-100 text-slate-400">
            <lucide-icon name="SearchX" [size]="21" />
          </div>
          <h3 class="mt-3 mb-0 text-sm font-semibold text-slate-700">ไม่พบยอดหักคืนค่าคอม</h3>
          <p class="mt-1 mb-0 text-xs text-slate-500">ลองเปลี่ยนคำค้นหา ช่วงวันที่ หรือตัวกรอง</p>
        </td>
      </tr>
    </ng-template>
  </p-table>
</section>

@if (selectedRecovery(); as recovery) {
  <div
    class="fixed inset-0 z-[230] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeHistory()"
    (keydown.escape)="closeHistory()"
  >
    <section
      class="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recovery-history-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">ประวัติยอดหักคืน</p>
          <h2 id="recovery-history-title" class="m-0 text-xl font-bold text-slate-800">
            {{ recovery.bookingCommission.commissionNo }} · {{ recovery.recipientUser.firstName }}
            {{ recovery.recipientUser.lastName }}
          </h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">
            ยอดหักคืนจากการคืนเงินของ {{ recovery.booking.bookingNo }}
          </p>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="closeHistory()"
        ><lucide-icon name="X" [size]="19" /></button>
      </div>

      <div class="grid gap-3 border-b border-slate-100 bg-slate-50 px-5 py-4 sm:grid-cols-3 sm:px-6">
        <div>
          <p class="m-0 text-xs font-medium text-slate-500">ยอดต้องหักคืน</p>
          <p class="mt-1 mb-0 text-lg font-bold text-red-600 tabular-nums">
            {{ recovery.recoveryAmount | number: '1.2-2' }} บาท
          </p>
        </div>
        <div>
          <p class="m-0 text-xs font-medium text-slate-500">หักคืนแล้ว</p>
          <p class="mt-1 mb-0 text-lg font-bold text-emerald-700 tabular-nums">
            {{ recovery.settledAmount | number: '1.2-2' }} บาท
          </p>
        </div>
        <div>
          <p class="m-0 text-xs font-medium text-slate-500">ยอดคงเหลือ</p>
          <p class="mt-1 mb-0 text-lg font-bold text-amber-700 tabular-nums">
            {{ recovery.remainingAmount | number: '1.2-2' }} บาท
          </p>
        </div>
      </div>

      <div class="max-h-[60vh] space-y-3 overflow-y-auto px-5 py-5 sm:px-6">
        <article class="rounded-xl border border-slate-200 bg-white p-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div class="flex flex-wrap items-center gap-2">
                <p class="m-0 font-semibold text-slate-800">{{ recovery.bookingRefund.refundNo }}</p>
                <span
                  class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                  [class]="recoveryStatusClass(recovery.status)"
                >{{ recoveryStatusLabel(recovery.status) }}</span>
              </div>
              <p class="mt-1 mb-0 text-xs text-slate-500">
                {{ recovery.bookingRefund.refundType === 'FULL' ? 'คืนเงินเต็มจำนวน' : 'คืนเงินบางส่วน' }}
                · {{ recovery.createdAt | date: 'dd MMM yyyy, HH:mm' }}
              </p>
              <p class="mt-2 mb-0 text-sm text-slate-600">{{ recovery.reason }}</p>
            </div>
            <div class="grid grid-cols-3 gap-x-4 text-right tabular-nums">
              <div>
                <p class="m-0 text-[11px] text-slate-500">ต้องหัก</p>
                <p class="mt-1 mb-0 font-semibold text-red-600">
                  {{ recovery.recoveryAmount | number: '1.2-2' }}
                </p>
              </div>
              <div>
                <p class="m-0 text-[11px] text-slate-500">หักแล้ว</p>
                <p class="mt-1 mb-0 font-semibold text-emerald-700">
                  {{ recovery.settledAmount | number: '1.2-2' }}
                </p>
              </div>
              <div>
                <p class="m-0 text-[11px] text-slate-500">คงเหลือ</p>
                <p class="mt-1 mb-0 font-semibold text-amber-700">
                  {{ recovery.remainingAmount | number: '1.2-2' }}
                </p>
              </div>
            </div>
          </div>

          <div class="mt-4 border-t border-slate-100 pt-3">
            <p class="m-0 text-xs font-semibold text-slate-600">ประวัติการนำยอดไปหัก</p>
            @for (application of recovery.applications; track application.id) {
              <div class="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs">
                <span class="font-medium text-slate-700">
                  {{ applicationTargetLabel(application) }} · {{ applicationTargetCode(application) }}
                </span>
                <span class="text-slate-500">{{ applicationStatusLabel(application.status) }}</span>
                <span class="font-semibold text-red-600 tabular-nums">
                  {{ application.amount | number: '1.2-2' }} บาท
                </span>
              </div>
            } @empty {
              <p class="mt-2 mb-0 text-xs text-slate-500">ยังไม่มีการนำยอดนี้ไปหักกับคอมมิชชั่นรอบใหม่</p>
            }
          </div>
        </article>
      </div>
      <div class="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-6">
        <button
          type="button"
          class="tw-button h-10 rounded-xl border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          (click)="closeHistory()"
        >ปิด</button>
      </div>
    </section>
  </div>
}
